import { useCallback, useRef, useState } from 'react'
import { useCurrentAccount, useCurrentNetwork, useDAppKit, useWallets, UiWallet } from '@mysten/dapp-kit-react'

import { useOnClickOutside } from 'utils/onClickOutside'
import { getWalletUniqueIdentifier } from 'utils/wallet'
import { AccountDropdownMenu } from './AccountDropdownMenu'
import { ConnectWalletMenu } from './ConnectWalletMenu'
import { usePostHogEntityAction } from 'utils/usePostHogEntityAction'
import { initializeSentrySession } from 'utils/sentry-utils'

interface Props {
  menuStyles?: React.CSSProperties
  menuClass?: string
}

export const ConnectWallet = ({ menuStyles, menuClass }: Props) => {
  const currentAccount = useCurrentAccount()

  const { capture } = usePostHogEntityAction()

  const network = useCurrentNetwork()

  const dAppKit = useDAppKit()

  const wallets = useWallets()

  const [isMenuOpen, setMenuOpen] = useState(false)
  const [selectedWallet, setSelectedWallet] = useState<UiWallet>()

  const connectWallet = useCallback(
    async (wallet: UiWallet) => {
      let accounts
      try {
        const res = await dAppKit.connectWallet({ wallet })
        accounts = res.accounts
      } catch {
        setSelectedWallet(undefined)
        return
      }

      // Post-success side-effects: their failures must not masquerade as a failed connect.
      capture('connect_wallet', {
        wallet_addresses: accounts.map(a => a.address),
        wallet_name: wallet.name,
      })
      initializeSentrySession(accounts.map(a => a.address)[0], network, wallet.name)
      setMenuOpen(false)
      setSelectedWallet(undefined)
    },
    [dAppKit, capture, network]
  )

  const onWalletSelect = useCallback(
    (wallet: UiWallet) => {
      if (getWalletUniqueIdentifier(selectedWallet) !== getWalletUniqueIdentifier(wallet)) {
        setSelectedWallet(wallet)
        void connectWallet(wallet)
      }
    },
    [connectWallet, selectedWallet]
  )

  const menuNode = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutside(menuNode, onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  if (currentAccount) {
    return <AccountDropdownMenu currentAccount={currentAccount} />
  }

  return (
    <ConnectWalletMenu
      toggleMenu={toggleMenu}
      isMenuOpen={isMenuOpen}
      menuNode={menuNode}
      wallets={wallets}
      menuStyles={menuStyles}
      menuClass={menuClass}
      onWalletSelect={onWalletSelect}
    />
  )
}
